import { ThemedText } from "@/components/ThemedText";
import { Button } from "@/components/ui/Button";
import CustomModal from "@/components/ui/CustomModal.web";
import React from "react";
import { useTranslation } from "react-i18next";
import { StyleSheet, View } from "react-native";

export type AlertAction = {
  text: string;
  onPress: () => void | Promise<void>;
  destructive?: boolean;
};

export interface AlertModalProps {
  visible: boolean;
  title: string;
  message: string;
  onClose?: () => void | Promise<void>;
  primaryAction?: AlertAction;
  secondaryAction?: AlertAction;
}

const AlertModal: React.FC<AlertModalProps> = ({
  visible,
  title,
  message,
  onClose,
  primaryAction,
  secondaryAction,
}) => {
  const { t } = useTranslation();
  const hasActions = !!(primaryAction || secondaryAction);
  const handleClose = () => {
    onClose?.();
  };

  return (
    <CustomModal
      visible={visible}
      onClose={handleClose}
      title={title}
      showCloseButton={false}
    >
      <ThemedText>{message}</ThemedText>
      {hasActions ? (
        <View style={styles.actionsRow}>
          {secondaryAction && (
            <View style={styles.actionButton}>
              <Button
                text={secondaryAction.text}
                onPress={secondaryAction.onPress}
                testID="alert-modal-secondary"
                style={
                  secondaryAction.destructive
                    ? styles.destructiveAction
                    : styles.neutralSecondaryAction
                }
                textStyle={
                  secondaryAction.destructive
                    ? undefined
                    : styles.neutralSecondaryText
                }
              />
            </View>
          )}
          {primaryAction && (
            <View style={styles.actionButton}>
              <Button
                text={primaryAction.text}
                onPress={primaryAction.onPress}
                testID="alert-modal-primary"
                style={
                  primaryAction.destructive
                    ? styles.destructiveAction
                    : undefined
                }
              />
            </View>
          )}
        </View>
      ) : (
        <View style={styles.singleAction}>
          <Button
            text={t("OK")}
            onPress={handleClose}
            testID="alert-modal-ok"
          />
        </View>
      )}
    </CustomModal>
  );
};

const styles = StyleSheet.create({
  actionsRow: {
    flexDirection: "row",
    gap: 12,
    marginTop: 20,
  },
  actionButton: {
    flex: 1,
  },
  singleAction: {
    marginTop: 20,
  },
  destructiveAction: {
    backgroundColor: "red",
  },
  // Default neutral styling for a non-destructive secondary action so it
  // reads clearly as "cancel / step back" against the primary CTA. Matches
  // the gray Cancel/Back buttons in other settings modals.
  neutralSecondaryAction: {
    backgroundColor: "#e5e7eb",
  },
  neutralSecondaryText: {
    color: "#333",
  },
});

export default AlertModal;
